import { Card } from "@/components/ui/card";
import ConsultationTableView from "./table";
import useDocumentTitle from "@/components/ui/common/document-title";
import Loader from "@/components/ui/loaders";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "@/redux/store";
import { setUserInfo, tokenReceived, User } from "@/redux/authSlice";
import { decodeJwtToken } from "@/lib/helper";

// Coaches open this page inside a Flutter webview without logging in. Their JWT
// is injected (stored in localStorage or passed as ?t=... in the URL) and the
// org_id lives only inside that token, not in the auth context. When the Redux
// user info is missing we hydrate it by decoding the token so all
// `useSelector(...userInfo?.user?.org_id)` reads keep working.
const hydrateUserFromToken = (token: string): Partial<User> | null => {
  const payload = decodeJwtToken(token) as Record<string, any> | null;
  if (!payload) return null;

  const orgId =
    payload.org_id ?? payload.organization_id ?? payload.organizationId;
  if (orgId == null) return null;

  const userId = payload.user_id ?? payload.id ?? payload.sub;

  return {
    id: userId != null ? Number(userId) : 0,
    org_id: Number(orgId),
    role_id: payload.role_id ?? payload.role,
    org_name: payload.org_name ?? payload.organization_name,
    first_name: payload.first_name || payload.name || "",
    email: payload.email || "",
  };
};

const Consultation = () => {
  useDocumentTitle("Consultations");
  const dispatch = useDispatch<AppDispatch>();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const userToken = useSelector((state: RootState) => state.auth.userToken);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Already hydrated (normal dashboard login) -> nothing to do.
    if (userInfo?.user?.org_id) return;

    const token =
      userToken ||
      localStorage.getItem("userToken") ||
      new URLSearchParams(window.location.search).get("t") ||
      "";

    if (!token) {
      console.info("[consultation] No token found to hydrate user context.");
      setHydrated(true);
      return;
    }

    const user = hydrateUserFromToken(token);
    if (!user) {
      console.info("[consultation] Could not extract org_id from token payload.");
      setHydrated(true);
      return;
    }

    // Ensure the token is in Redux so fetchBaseQuery attaches the Bearer header.
    if (!userToken) {
      dispatch(tokenReceived(token));
    }

    dispatch(setUserInfo(user));
    console.info("[consultation] Hydrated user context from token:", user);
    setHydrated(true);
  }, [dispatch, userInfo, userToken]);

  // Do NOT render the table (which fires consultation queries with org_id=0)
  // until the user context has been hydrated from the token.
  if (!userInfo?.user?.org_id && !hydrated) {
    return <Loader open />;
  }

  return (
    <div className="w-full p-5">
      <Card className="pt-4">
        <ConsultationTableView />
      </Card>
    </div>
  );
};

export default Consultation;